import { useShouldRemoveFileUploadPrefix } from '@modules/common/hooks';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { IntegrationChannelType } from 'generated/graphql';
import { Feedback } from 'v2/core/entities/feedback';
import { trackFeedbackEvents } from '../../../utils';
import SourceIcon from '../../FeedbackCard/FeedbackSourceIcon';

interface FeedbackDetailsHeaderSourceDetailProps {
  feedback: Feedback;
  sourceV2: Feedback['sourceV2'];
  isLoadingMore?: boolean;
}

export const FeedbackDetailsHeaderSourceDetail = ({
  feedback,
  sourceV2,
  isLoadingMore,
}: FeedbackDetailsHeaderSourceDetailProps) => {
  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();

  const externalLinkNode = () => {
    return (
      <Tooltip
        title={
          !!sourceV2?.externalLink
            ? 'Click to go to source'
            : 'Source link not available'
        }
        placement="top"
        arrow
      >
        <Box component="span">
          <IconButton
            aria-label="Go to source"
            href={!!sourceV2?.externalLink ? sourceV2?.externalLink : ''}
            target="_blank"
            rel="noreferrer"
            size="small"
            disabled={!sourceV2?.externalLink}
            onClick={() => {
              trackFeedbackEvents({
                eventName: 'Feedback External Source Link Clicked',
                feedbackID: feedback.id,
                sourceCreatedAt: feedback.sourceCreatedAt,
                sourceName: feedback?.sourceV2?.name,
              });
            }}
          >
            <OpenInNewRoundedIcon />
          </IconButton>
        </Box>
      </Tooltip>
    );
  };

  if (isLoadingMore) {
    return null;
  }

  const getNamespacedSourceName = (sourceName: string) => {
    const namespace = feedback.namespace;
    return namespace ? `${sourceName} ${namespace}`.trim() : sourceName;
  };

  const getSourceName = () => {
    /* For splitting "ZENDESK_SUPPORT" to "ZENDESK SUPPORT" */
    const sourceName =
      shouldRemoveFileUploadPrefix &&
      feedback.sourceV2?.integrationChannelType ===
        IntegrationChannelType.FileUpload
        ? ''
        : feedback.sourceV2?.name ?? '';

    return getNamespacedSourceName(sourceName.trim());
  };

  return (
    <>
      <SourceIcon sourceV2={feedback.sourceV2} />
      <Typography variant="subtitle2">{getSourceName()}</Typography>
      {externalLinkNode()}
    </>
  );
};
